/**
 * /app/{tenantSlug}/settings/roles — Settings → custom / granular RBAC.
 *
 * Batch 32 Track B. HR-admin (or anyone holding role.admin) defines custom
 * roles and assigns capabilities via a module × action matrix. Custom roles
 * AUGMENT the fixed role — they only ADD capabilities. A `dangerous` token
 * (flagged) requires a step-up 2FA code to assign.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, PlusCircle, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import {
  useCapabilityCatalog,
  useCreateCustomRole,
  useCustomRoles,
  useDeleteCustomRole,
  useEffectiveCapabilities,
  useUpdateCustomRole,
} from "@/hooks/use-custom-rbac";
import type { Capability, CustomRole } from "@/lib/api/custom-roles";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/roles",
)({
  component: SettingsRolesRoute,
});

// ---------------------------------------------------------------------------
// Role editor dialog (create + edit share the matrix builder)
// ---------------------------------------------------------------------------

interface RoleEditorProps {
  catalog: Capability[];
  /** Tokens the CURRENT user effectively holds — others are non-grantable. */
  myCaps: string[];
  bypass: boolean;
  existing?: CustomRole;
  trigger: React.ReactNode;
}

function RoleEditor({ catalog, myCaps, bypass, existing, trigger }: RoleEditorProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(
    new Set(existing?.capabilities ?? []),
  );
  const [totp, setTotp] = useState("");

  const create = useCreateCustomRole();
  const update = useUpdateCustomRole();
  const pending = create.isPending || update.isPending;

  const canGrant = (token: string) => bypass || myCaps.includes(token);
  const needsTotp = useMemo(
    () => catalog.some((c) => c.dangerous && selected.has(c.token)),
    [catalog, selected],
  );

  // Group catalog by module for the matrix.
  const byModule = useMemo(() => {
    const m = new Map<string, Capability[]>();
    for (const c of catalog) {
      const arr = m.get(c.module) ?? [];
      arr.push(c);
      m.set(c.module, arr);
    }
    return [...m.entries()];
  }, [catalog]);

  const toggle = (token: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return next;
    });
  };

  const reset = () => {
    setName(existing?.name ?? "");
    setDescription(existing?.description ?? "");
    setSelected(new Set(existing?.capabilities ?? []));
    setTotp("");
  };

  const onSubmit = async () => {
    const body = {
      name: name.trim(),
      description: description.trim() || undefined,
      capabilities: [...selected],
      totp_code: totp.trim() || undefined,
    };
    try {
      if (existing) await update.mutateAsync({ id: existing.id, body });
      else await create.mutateAsync(body);
      toast.success(existing ? "Role updated." : "Role created.");
      setOpen(false);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.name}` : "Create custom role"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="role-name">Name *</Label>
            <Input
              id="role-name"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Payroll Operator"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="role-desc">Description</Label>
            <Input
              id="role-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What can this role do?"
            />
          </div>

          <div className="space-y-3">
            <Label>Capabilities</Label>
            <p className="text-xs text-muted-foreground">
              Custom roles only ADD permissions on top of a member&apos;s base
              role. You can only grant capabilities you hold yourself.
            </p>
            {byModule.map(([moduleName, caps]) => (
              <div key={moduleName} className="rounded-md border p-3">
                <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                  {moduleName}
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {caps.map((c) => {
                    const grantable = canGrant(c.token);
                    return (
                      <label
                        key={c.token}
                        className="flex items-start gap-2 text-sm"
                        title={grantable ? c.description : "You do not hold this capability"}
                      >
                        <Checkbox
                          checked={selected.has(c.token)}
                          disabled={!grantable}
                          onCheckedChange={() => toggle(c.token)}
                          className="mt-0.5"
                        />
                        <span className={grantable ? "" : "opacity-50"}>
                          {c.label}
                          {c.dangerous && (
                            <Badge variant="destructive" className="ml-1.5 text-[10px]">
                              high-risk
                            </Badge>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {needsTotp && (
            <div className="space-y-1.5">
              <Label htmlFor="role-totp" className="flex items-center gap-1.5">
                <ShieldAlert className="h-4 w-4 text-destructive" />
                2FA code (required for high-risk capabilities)
              </Label>
              <Input
                id="role-totp"
                value={totp}
                inputMode="numeric"
                maxLength={6}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={pending}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={pending || name.trim() === ""}>
              {pending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {existing ? "Save changes" : "Create role"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

function SettingsRolesRoute() {
  const catalogQ = useCapabilityCatalog();
  const rolesQ = useCustomRoles();
  const { caps: myCaps, bypass, has } = useEffectiveCapabilities();
  const del = useDeleteCustomRole();

  const catalog = catalogQ.data?.items ?? [];
  const roles = rolesQ.data?.items ?? [];
  const canAdmin = bypass || has("role.admin");

  const onDelete = async (r: CustomRole) => {
    try {
      await del.mutateAsync(r.id);
      toast.success("Role deleted.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Custom roles</h2>
              <p className="text-sm text-muted-foreground">
                Define narrow roles by granting specific capabilities. Custom
                roles add to a member&apos;s base role; they never remove access.
              </p>
            </div>
            {canAdmin && (
              <RoleEditor
                catalog={catalog}
                myCaps={myCaps}
                bypass={bypass}
                trigger={
                  <Button size="sm" className="gap-1.5">
                    <PlusCircle className="h-4 w-4" />
                    New role
                  </Button>
                }
              />
            )}
          </div>

          {!canAdmin && (
            <Alert>
              <AlertTitle>Restricted</AlertTitle>
              <AlertDescription>
                You need the role administration capability to manage custom roles.
              </AlertDescription>
            </Alert>
          )}

          {(catalogQ.isLoading || rolesQ.isLoading) && (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {rolesQ.data && roles.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No custom roles yet.
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {roles.map((r) => (
              <Card key={r.id}>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="text-base">{r.name}</CardTitle>
                      {r.description && (
                        <p className="mt-1 text-xs text-muted-foreground">{r.description}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {r.capabilities.length === 0 && (
                          <span className="text-xs text-muted-foreground">No capabilities</span>
                        )}
                        {r.capabilities.map((c) => (
                          <Badge key={c} variant="secondary" className="font-mono text-[10px]">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {canAdmin && (
                      <div className="flex items-center gap-2">
                        <RoleEditor
                          catalog={catalog}
                          myCaps={myCaps}
                          bypass={bypass}
                          existing={r}
                          trigger={
                            <Button variant="outline" size="sm">
                              Edit
                            </Button>
                          }
                        />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete role &quot;{r.name}&quot;?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Members will lose its capabilities. This cannot
                                be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDelete(r)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </SettingsShell>
    </>
  );
}
